import {combine, forward, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersList, initSortingsList} from 'core/hooks/grid2.0';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    clearDataCacheFx,
    CommonGate,
    data$,
    dataUngrouped$,
    fetchDataFx,
    fetchDataUngroupedFx,
    filters$,
    filtersChanged,
    filtersReset,
    filtersSubmit,
    Gate,
    hasNext$,
    listStateChanged,
    pageIncremented,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    totalCount$,
    UngroupedGate,
} from './index';

data$
    .on(fetchDataFx.doneData, (state, {content}) => (state ? state.concat(content) : content))
    .reset(sortingsChanged, filtersSubmit, Gate.close, CommonGate.close);

dataUngrouped$
    .on(fetchDataUngroupedFx.doneData, (state, {content}) =>
        state ? state.concat(content) : content,
    )
    .reset(sortingsChanged, filtersSubmit, Gate.close, UngroupedGate.close);

pageNumber$.reset(
    filtersSubmit,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    stateReset,
    CommonGate.close,
    UngroupedGate.close,
);
pageSize$.reset(stateReset, CommonGate.close, UngroupedGate.close);

totalCount$
    .on(
        merge([fetchDataFx.doneData, fetchDataUngroupedFx.doneData]),
        (state, {totalCount}) => totalCount,
    )
    .reset(stateReset, CommonGate.close, UngroupedGate.close);
hasNext$
    .on(merge([fetchDataFx.doneData, fetchDataUngroupedFx.doneData]), (state, {hasNext}) => hasNext)
    .reset(stateReset, CommonGate.close, UngroupedGate.close);

initFiltersList(filters$, filtersChanged, filtersReset, stateReset);
initSortingsList(sortings$, sortingsChanged, sortingsReset, stateReset);

sample({
    clock: pageIncremented,
    source: pageNumber$.map((state) => state + 1),
    target: pageNumberChanged,
});

forward({
    from: merge([
        Gate.close,
        pageSizeChanged,
        filtersSubmit,
        filtersReset,
        sortingsChanged,
        sortingsReset,
    ]),
    to: clearDataCacheFx,
});

sample({
    clock: CommonGate.open,
    target: clearDataCacheFx,
});

sample({
    clock: UngroupedGate.open,
    target: clearDataCacheFx,
});

sample({
    clock: merge([Gate.open, listStateChanged]),
    source: combine({
        sortings: sortings$,
        filters: filters$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    fn: ({filters, sortings, pageNumber, pageSize}) => ({
        paging: {
            pageSize,
            pageNumber,
        },
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
    }),
    target: fetchDataFx,
});

sample({
    clock: merge([Gate.open, listStateChanged]),
    source: combine({
        sortings: sortings$,
        filters: filters$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    fn: ({filters, sortings, pageNumber, pageSize}) => ({
        paging: {
            pageSize,
            pageNumber,
        },
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
    }),
    target: fetchDataUngroupedFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
fetchDataUngroupedFx.fail.watch(({error}) => showRestError(error as RestError));
